import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/components/Placeholder";
import HeroRotator from "@/components/HeroRotator";
import { LOGO, TAGLINE } from "@/lib/sections";

export default function Cover() {
  return (
    <section
      id="cover"
      data-testid="section-cover"
      className="deck-section grain relative flex items-stretch bg-ink overflow-hidden"
    >
      <div className="mx-auto grid w-full max-w-[1600px] grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 px-6 md:px-12 lg:px-16 pt-28 md:pt-32 pb-16">
        {/* Left — copy */}
        <div className="lg:col-span-6 flex flex-col justify-between relative z-10">
          {/* Brand mark — strategically large at the top of the deck */}
          <Reveal>
            <div className="flex flex-col">
              <img
                data-testid="cover-logo"
                src={LOGO.mark}
                alt="Aarya24kt — Gold Foil Artwork Collection"
                crossOrigin="anonymous"
                className="h-40 md:h-52 lg:h-60 w-auto -ml-3 md:-ml-4 select-none object-contain"
                style={{ maxWidth: "min(420px, 100%)" }}
              />
              <div
                data-testid="cover-tagline"
                className="-mt-2 md:-mt-3 font-heading italic text-gold text-lg md:text-xl lg:text-2xl tracking-wide"
              >
                {TAGLINE}
              </div>
              <div className="mt-6 h-px w-24 bg-gold/40" />
            </div>
          </Reveal>

          <div className="mt-10 lg:mt-0">
            <Reveal delay={0.2}>
              <div className="num-overline">01 · The Brand</div>
            </Reveal>
            <Reveal delay={0.3}>
              <h1 className="mt-6 font-heading text-ivory text-5xl md:text-6xl lg:text-[88px] xl:text-[96px] leading-[0.95] tracking-tightest">
                The Art
                <br />
                of <span className="italic text-gold">Gifting</span>
                <br />
                in <span className="italic">Gold.</span>
              </h1>
            </Reveal>

            <Reveal delay={0.45}>
              <p className="mt-8 max-w-md font-body text-base md:text-lg text-beige/80 leading-relaxed">
                Luxury gifting objects inspired by Indian heritage, celebration
                and emotion.
              </p>
            </Reveal>
          </div>

          {/* Stats */}
          <Reveal delay={0.6}>
            <div className="mt-12 grid grid-cols-3 gap-6 border-t border-gold/15 pt-8">
              {[
                { value: "16+", label: "Years of Craft" },
                { value: "3500+", label: "Retail Partners" },
                { value: "2200+", label: "Designs" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-heading text-3xl md:text-4xl lg:text-5xl text-ivory">
                    {s.value}
                  </div>
                  <div className="mt-2 num-overline text-ivory/50">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right — product on a luxury contrasting display panel */}
        <div className="lg:col-span-6 flex items-center">
          <HeroRotator
            aspect="aspect-[4/5] lg:aspect-[3/4]"
            delay={0.2}
            slides={[
              {
                src: IMAGES.heroPeacock,
                mode: "raw",
                alt: "Aarya24KT — 24KT Gold Peacock framed artwork",
                ariaLabel: "Featured artwork — Peacock frame",
                // Soft warm glow around the cream frame so it doesn't float harshly on dark bg
                filter:
                  "drop-shadow(0 0 60px rgba(201,165,94,0.08)) drop-shadow(0 30px 60px rgba(200,164,106,0.18)) drop-shadow(0 12px 24px rgba(0,0,0,0.6))",
              },
              {
                src: IMAGES.hero,
                mode: "clean",
                alt: "Aarya24KT — 24KT Gold Ganesha · Fine Gold 99.99",
                ariaLabel: "Featured artwork — 24KT Ganesha sculpture",
                threshold: 232,
                soft: 26,
                filter:
                  "drop-shadow(0 36px 64px rgba(200,164,106,0.22)) drop-shadow(0 14px 28px rgba(0,0,0,0.7))",
              },
            ]}
          />
        </div>
      </div>

      {/* Bottom hairline & meta */}
      <div className="no-print absolute bottom-6 left-6 right-6 md:left-12 md:right-12 lg:left-16 lg:right-16 flex items-center justify-between text-ivory/40">
        <span className="num-overline">Mumbai · London · Dubai · New York</span>
        <span className="num-overline">An International Presentation · 2026</span>
      </div>
    </section>
  );
}
